import { useCallback, useSyncExternalStore } from "react";

import { AnalyticsContext, NavigationEvent } from "@salestrak/analytics-taxonomy";
import { trackEvent } from "#app/lib/analytics/analytics-session";

/**
 * The open/closed state of one app-level dialog, plus the payload the last
 * `open` carried. The payload survives `close` so a closing animation keeps
 * its content instead of flashing empty.
 */
export type DialogState<TPayload> = {
  open: boolean;
  payload: TPayload | null;
  /** True once the dialog has opened at all; a lazy mount keys on it. */
  everOpened: boolean;
};

export type DialogEmitter<TPayload> = {
  /** Open the dialog. Callable from anywhere: a keydown handler, a toast
   *  action, a menu inside a portal that cannot read React context. */
  open: (...args: TPayload extends void ? [] : [payload: TPayload]) => void;
  close: () => void;
  /** Runs on every `open` call with its payload. For a mount that reacts to
   *  the request itself (a lookup before deciding to show anything). */
  onOpen: (listener: (payload: TPayload) => void) => () => void;
  /** The mount's read of the state, over `useSyncExternalStore`. */
  useDialogState: () => DialogState<TPayload> & {
    setOpen: (open: boolean) => void;
  };
};

/**
 * One store per app-level dialog. An `open` before the mount exists is not
 * lost: the store holds it and the mount reads it on its first render, which
 * is how a deep link that fires before the workspace layout renders still
 * opens its dialog.
 *
 * `name` is the dialog's analytics key: each closed-to-open change sends
 * `navigation.open_dialog` with it, so a dialog that replaced a routed page
 * keeps a usage signal. It is a stable snake_case name, never an id.
 */
export function createDialogEmitter<TPayload = void>(name: string): DialogEmitter<TPayload> {
  let state: DialogState<TPayload> = {
    open: false,
    payload: null,
    everOpened: false,
  };
  const listeners = new Set<() => void>();
  const openListeners = new Set<(payload: TPayload) => void>();

  function set(next: DialogState<TPayload>) {
    if (next.open && !state.open) {
      trackEvent({
        context: AnalyticsContext.Navigation,
        event: NavigationEvent.OpenDialog,
        data: { dialog: name },
      });
    }
    state = next;
    for (const listener of listeners) listener();
  }
  function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }
  function getSnapshot() {
    return state;
  }
  function close() {
    if (!state.open) return;
    set({ ...state, open: false });
  }
  function open(...args: unknown[]) {
    const payload = (args.length > 0 ? args[0] : null) as TPayload | null;
    set({ open: true, payload, everOpened: true });
    if (payload !== null) {
      for (const listener of openListeners) listener(payload);
    }
  }

  return {
    open: open as DialogEmitter<TPayload>["open"],
    close,
    onOpen(listener) {
      openListeners.add(listener);
      return () => {
        openListeners.delete(listener);
      };
    },
    useDialogState() {
      const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
      const setOpen = useCallback((next: boolean) => {
        if (next) set({ ...state, open: true, everOpened: true });
        else close();
      }, []);
      return { ...snapshot, setOpen };
    },
  };
}
